import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Icon } from './Icon';

export type BadgeTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'primary'
  | 'gold'
  | 'hifz-none'
  | 'hifz-learning'
  | 'hifz-memorized'
  | 'hifz-weak'
  | 'hifz-mastered';
export type BadgeAppearance = 'tinted' | 'solid' | 'outline';

const TINTED: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-muted',
  info: 'bg-info-subtle text-info-text',
  success: 'bg-success-subtle text-success-text',
  warning: 'bg-warning-subtle text-warning-text',
  danger: 'bg-danger-subtle text-danger-text',
  primary: 'bg-primary-subtle text-on-primary-subtle',
  gold: 'bg-accent-subtle text-accent-text',
  'hifz-none': 'bg-hifz-none text-ink-950 border border-border',
  'hifz-learning': 'bg-hifz-learning text-ink-950',
  'hifz-memorized': 'bg-hifz-memorized text-ink-950',
  'hifz-weak': 'bg-hifz-weak text-ink-950',
  'hifz-mastered': 'hifz-cell-mastered text-paper-0',
};

const SOLID: Partial<Record<BadgeTone, string>> = {
  neutral: 'bg-ink-600 text-paper-0',
  info: 'bg-info text-on-info',
  success: 'bg-success text-on-success',
  warning: 'bg-warning text-on-warning',
  danger: 'bg-danger text-on-danger',
  primary: 'bg-primary text-on-primary',
  gold: 'bg-accent text-on-accent',
};

const OUTLINE: Partial<Record<BadgeTone, string>> = {
  neutral: 'border border-border-strong text-muted',
  info: 'border border-info text-info-text',
  success: 'border border-success text-success-text',
  warning: 'border border-warning text-warning-text',
  danger: 'border border-danger text-danger-text',
  primary: 'border border-primary text-brand',
  gold: 'border border-gold-500 text-accent-text',
};

export function badgeToneClasses(tone: BadgeTone, appearance: BadgeAppearance): string {
  if (appearance === 'solid') return SOLID[tone] ?? TINTED[tone];
  if (appearance === 'outline') return OUTLINE[tone] ?? TINTED[tone];
  return TINTED[tone];
}

export interface BadgeProps {
  tone?: BadgeTone;
  appearance?: BadgeAppearance;
  icon?: LucideIcon | undefined;
  iconClassName?: string | undefined;
  children: ReactNode;
  className?: string;
  /** Pulsing dot before the label ("live" only; static under reduced motion). */
  pulse?: boolean;
}

/** System status pill: always icon + text, never colour alone (design system §4.11). */
export function Badge({ tone = 'neutral', appearance = 'tinted', icon, iconClassName, children, className, pulse }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex h-6 max-w-full items-center gap-1 whitespace-nowrap rounded-full px-2 text-xs font-medium',
        badgeToneClasses(tone, appearance),
        className,
      )}
    >
      {pulse ? (
        <span aria-hidden="true" className="relative flex size-2">
          <span className="live-dot absolute inset-0 rounded-full bg-danger" />
          <span className="relative size-2 rounded-full bg-danger" />
        </span>
      ) : null}
      {icon ? <Icon icon={icon} size={14} className={iconClassName} /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}

/** User data chips (specialties, languages) — not statuses. */
export function Tag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex h-6 items-center rounded-sm bg-surface-sunken px-2 text-xs text-secondary', className)}>
      {children}
    </span>
  );
}
